<template>
  <div class="create-offer-view">
    <offer-view-base-header
      :offer-recipient="customerOrganizationName"
      @go-back="goBack"
    />
    <template v-if="supplier && state.pageCms">
      <div class="create-offer-view__title-wrapper">
        <h1 class="create-offer-view__title">{{ pageTitle }}</h1>
        <button
          class="create-offer-view__new-tab-button"
          @click="handleOnNewTabClick"
        >
          <img :src="`/images/new_tab.svg`" alt="new-tab" />
          {{ howToCreateButtonText }}
        </button>
      </div>
      <div class="create-offer-view__body">
        <blank-offer-item
          :page-cms="state.pageCms"
          @new-offer="createNewOffer"
        />
        <template v-if="state.cloneableOffers">
          <div
            v-if="state.cloneableOffers.length"
            class="create-offer-view__previous-offers"
          >
            <reuse-previous-offers
              :page-cms="state.pageCms"
              :service-offers="serviceOffers"
              :cloneable-offers="state.cloneableOffers"
              :supplier-id="supplier.id"
              @new-offer="createNewReusableOffer"
            />
          </div>
        </template>
        <div v-else class="create-offer-view__loader">
          <b-spinner type="grow" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import { RouteNames } from "@/router/types";

import OfferViewBaseHeader from "@/components/OfferViewBaseHeader/OfferViewBaseHeader.vue";
import BlankOfferItem from "./BlankOfferItem.vue";
import ReusePreviousOffers from "./PreviousOffers.vue";
import { useTenderApi } from "@/services/api/useTenderApi";
import cmsService from "@/services/cmsService";
import { usePriceRequestCache } from "@/composables/usePriceRequestCache";
import { useOfferTerminology } from "@/composables/useOfferTerminology";

import type {
  CloneableOffer,
  PriceRequestDetails,
} from "@/stores/tender/types";
import { Pages } from "@/stores/cms/types";

const props = defineProps<{
  supplier:
    | {
        id: number;
      }
    | undefined
    | null;
}>();

const router = useRouter();
const route = useRoute();
const tenderApis = useTenderApi();
const priceRequestCache = usePriceRequestCache();
const { setEstimateMode, offerTerm, offerTermCapitalized } =
  useOfferTerminology();

const state = reactive({
  priceRequest: undefined as PriceRequestDetails | undefined,
  pageCms: undefined as Pages | undefined,
  cloneableOffers: undefined as Array<CloneableOffer> | undefined,
});

// Terminology based on priceRequest.requestEstimates
const isEstimateMode = computed(() => {
  // Check if requestEstimates exists and is true
  return state.priceRequest && "requestEstimates" in state.priceRequest
    ? !!state.priceRequest.requestEstimates
    : false;
});
const howToCreateButtonText = computed(
  () => `Hvordan lage ${offerTerm.value}?`,
);

const customerOrganizationName = computed(() => {
  return (route.query.orgName as string) || "";
});
const suggestedName = computed(() => {
  return (route.query.nickName as string) || "";
});
const pageTitle = computed(() => {
  if (state.pageCms?.title) {
    // If the title contains "tilbud", replace it with the appropriate term
    return isEstimateMode.value
      ? state.pageCms.title.replace(/tilbud/i, "estimat")
      : state.pageCms.title;
  }
  return "";
});

const serviceOffers = computed(() => {
  return state.cloneableOffers?.map((offer) => ({
    id: offer.serviceOfferId,
    tenderId: offer.tenderId,
    title: offer.nickname || `${offerTermCapitalized.value} på tjenestetype`,
    subtitle: `${offer.serviceCategoryLabel} - ${offer.customerOrgName} - ${offer.customerPropertyAddress}`,
    serviceOffer: {
      id: offer.serviceOfferId,
      tenderId: offer.tenderId,
    },
  }));
});
const priceRequestId = computed(() => {
  return parseInt((route.params.priceRequestId || "-1") as string);
});

// Methods
const fetchPageCms = async () => {
  const response = await cmsService.getCreateOfferPage();
  state.pageCms = response[0];
};
const fetchCloneableOffers = async (priceRequest: PriceRequestDetails) => {
  try {
    const response = await tenderApis.getCloneableOffers({
      supplierId: props.supplier?.id || -1,
      serviceCategoryId: priceRequest.serviceCategoryId,
    });
    state.cloneableOffers = response?.data;
  } catch (error) {
    console.error("Error fetching cloneable offers:", error);
  }
};
const createNewReusableOffer = (offer: CloneableOffer) => {
  const nickname = offer.nickname;
  router.replace({
    name: RouteNames.EDIT_OFFER,
    query: {
      reuseOfferId: offer.serviceOfferId,
      suggestedName: nickname ? `${nickname} (Kopi)` : "",
    },
  });
};
const createNewOffer = () => {
  router.replace({
    name: RouteNames.EDIT_OFFER,
    query: {
      suggestedName: suggestedName.value || "",
    },
  });
};
const goBack = () => router.back();
const handleOnNewTabClick = () => {
  window.open(import.meta.env.VITE_APP_NEED_HELP_OFFER_URL, "_blank");
};
const initialize = async () => {
  fetchPageCms().then(() => {});
  if (priceRequestId.value < 0) {
    return router.push({ name: RouteNames.SUPPLIER_ADMIN });
  }

  const priceRequestData =
    await priceRequestCache.getPriceRequestDetailsWithCache(
      props.supplier?.id || -1,
      priceRequestId.value,
    );

  if (priceRequestData) {
    state.priceRequest = priceRequestData;
    // Set estimate mode based on price request data
    setEstimateMode(!!priceRequestData.requestEstimates);

    // Only fetch cloneable offers if we have price request data
    if (state.priceRequest) {
      fetchCloneableOffers(state.priceRequest).then(() => {});
      console.log("Price request", state.priceRequest);
    }
  }
};
initialize();
</script>

<style lang="scss" scoped>
.create-offer-view {
  padding: 4.75rem 0;

  &__title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    max-width: 37.125rem;
    margin: 3rem auto 0;
  }

  &__title {
    font-weight: 600;
    font-size: $font-size-2xl;
  }

  &__new-tab-button {
    display: flex;
    height: 40px;
    padding: 0 16px;
    align-items: center;
    gap: 8px;
    border-radius: 4px;
    background: var(--Yellow-Dark, #ecba4e);
    border: none;
    cursor: pointer;
    transition: background-color 0.3s;
    color: var(--Black, #1d1d1d);
    font-weight: 700;

    img {
      width: 24px;
      height: 24px;
    }

    &:hover {
      background: rgba(236, 186, 78, 0.8);
    }

    &:active {
      background: rgba(236, 186, 78, 0.6);
    }
  }

  &__body {
    max-width: 37.125rem;
    margin: 3rem auto 0;
  }

  &__previous-offers {
    margin-top: 2.75rem;
  }

  &__loader {
    margin-top: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

:deep(*) {
  font-family: "Montserrat", sans-serif;
  text-align: left;
}
</style>
